import React, {type EventHandler, type MouseEvent, type ReactNode} from 'react';
import {FormattedMessage} from 'react-intl';

import {type Post} from 'mattermost-redux/types/posts';

import Constants from 'utils/constants';
import * as PostUtils from 'utils/post_utils';
import PostInfo from 'components/post_view/post_info';
import UserProfile from 'components/user_profile';

import {Badge, BotBadge} from '@time-webkit/all/atoms';

import PostHeaderCustomStatus from './post_header_custom_status';

import './post_header.scss';

export type Props = {

    /*
     * The post to render the header for
     */
    post: Post;

    /*
     * Function called when the comment icon is clicked
     */
    handleReplyInThreadClick: EventHandler<MouseEvent>;

    /*
     * Function called when the card icon is clicked
     */
    handleCardClick: (post: Post) => void;

    /*
     * Function called when the post options dropdown is opened
     */
    handleDropdownOpened: (opened: boolean) => void;

    /*
     * Set to render compactly
     */
    compactDisplay?: boolean;

    /**
     * Set to indicate that this is previous post was not a reply to the same thread
     */
    isFirstReply?: boolean;

    /**
     * Set to mark post as being hovered over
     */
    hover: boolean;

    /*
     * Set to render the post time when not hovering
     */
    showTimeWithoutHover: boolean;

    postTimeSlot?: ReactNode;

    /**
     * If the user that made the post is a bot.
     */
    isBot: boolean;

    /**
     * To Check if the current post is last in the list
     */
    isLastPost?: boolean;
    isSending?: boolean;
    overrideUsername?: string;
    overrideIconURL?: string;
    onProfilePopoverOpen?: () => void;
};

const PostHeader: React.FC<Props> = (props) => {
    const {
        overrideUsername,
        overrideIconURL,
        post,
        isBot,
        handleReplyInThreadClick,
        handleCardClick,
        handleDropdownOpened,
        compactDisplay,
        isFirstReply,
        showTimeWithoutHover,
        postTimeSlot,
        hover,
        isLastPost,
        isSending,
        onProfilePopoverOpen,
    } = props;
    const isSystemMessage = PostUtils.isSystemMessage(post);
    const fromAutoResponder = PostUtils.fromAutoResponder(post);
    const fromWebhook = post?.props?.from_webhook === 'true';

    let userProfile = (
        <UserProfile
            userId={post.user_id}
            hasMention={true}
            onProfilePopoverOpen={onProfilePopoverOpen}
        />
    );
    let indicator;

    if (fromWebhook) {
        userProfile = (
            <UserProfile
                userId={post.user_id}
                hideStatus={true}
                overwriteName={overrideUsername}
                overwriteIcon={overrideIconURL}
                onProfilePopoverOpen={onProfilePopoverOpen}
            />
        );

        if (!isBot) {
            indicator = <BotBadge />;
        }
    } else if (fromAutoResponder) {
        userProfile = (
            <UserProfile
                userId={post.user_id}
                hideStatus={true}
                hasMention={true}
                onProfilePopoverOpen={onProfilePopoverOpen}
            />
        );

        indicator = (
            <Badge>
                <FormattedMessage
                    id='post_info.auto_responder'
                    defaultMessage='AUTOMATIC REPLY'
                />
            </Badge>
        );
    } else if (isSystemMessage && isBot) {
        userProfile = (
            <UserProfile
                userId={post.user_id}
                hideStatus={true}
            />
        );
    } else if (isSystemMessage) {
        userProfile = (
            <UserProfile
                overwriteName={
                    <FormattedMessage
                        id='post_info.system'
                        defaultMessage='System'
                    />
                }
                overwriteImage={Constants.SYSTEM_MESSAGE_PROFILE_IMAGE}
                disablePopover={true}
            />
        );
    }

    const customStatus = (
        <PostHeaderCustomStatus
            userId={post.user_id}
            isBot={isBot || fromWebhook}
            isSystemMessage={isSystemMessage}
        />
    );

    return (
        <div className='post__header'>
            <div className='col col__name'>
                {userProfile}
                {indicator}
                {customStatus}
            </div>
            <div className='col'>
                <PostInfo
                    post={post}
                    handleReplyInThreadClick={handleReplyInThreadClick}
                    handleCardClick={handleCardClick}
                    handleDropdownOpened={handleDropdownOpened}
                    compactDisplay={compactDisplay}
                    isFirstReply={isFirstReply}
                    showTimeWithoutHover={showTimeWithoutHover}
                    postTimeSlot={postTimeSlot}
                    hover={hover}
                    isLastPost={isLastPost}
                    isSending={isSending}
                />
            </div>
        </div>
    );
};

export default PostHeader;
